'use client';

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form';
import useUser from '../../hooks/useUser';
import { handleLoginError } from '@/utils/login';
import GoogleIcon from './GoogleIcon';
import ForgotPasswordModal from './ForgotPasswordModal';
import { Link } from '@tanstack/react-router';
import { PRESSABLE_CLASS } from './AuthPageShell';
import { cn } from '@/lib/utils';

const loginSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
});

type LoginFormValues = z.infer<typeof loginSchema>;

function AuthForm({ returnTo }: { returnTo?: string }) {
  const { login, isAuthenticating, loginGoogle } = useUser();
  const [forgotPasswordModalOpen, setForgotPasswordModalOpen] = useState(false);

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const onSubmit = (values: LoginFormValues) => {
    login(
      {
        email: values.email.replace(/\s+/g, ''),
        password: values.password,
      },
      {
        onError: handleLoginError,
        onSuccess: () => { },
      },
    );
  };

  return (
    <div className="w-full max-w-sm">
      <ForgotPasswordModal
        open={forgotPasswordModalOpen}
        onOpenChange={setForgotPasswordModalOpen}
      />

      <div className="mb-6 space-y-1">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
          Sign in
        </h2>
        <p className="text-sm text-muted-foreground">
          Voice sessions to learn anything, or to screen people.
        </p>
      </div>

      <div className="space-y-4">
        <GoogleButton
          onClick={() => {
            loginGoogle();
          }}
          className="w-full">
          Continue with Google
        </GoogleButton>

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t" />
          </div>
          <div className="relative flex justify-center text-xs">
            <span className="bg-white dark:bg-gray-950 px-3 text-muted-foreground">or</span>
          </div>
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-3">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      autoFocus
                      type="email"
                      placeholder="Email"
                      className="h-10 rounded-sm"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      type="password"
                      placeholder="Password"
                      className="h-10 rounded-sm"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button
              type="submit"
              disabled={isAuthenticating}
              className={cn(
                'w-full h-10 rounded-sm bg-gray-900 hover:bg-gray-800 text-white dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100 font-medium',
                PRESSABLE_CLASS,
              )}>
              {isAuthenticating ? 'Signing in...' : 'Sign in'}
            </Button>

            <div className="flex items-center justify-between text-xs text-muted-foreground pt-1">
              <button
                type="button"
                onClick={() => setForgotPasswordModalOpen(true)}
                className="hover:text-primary transition-colors">
                Forgot password?
              </button>
              <Link
                to="/register"
                search={returnTo ? { returnTo } : undefined}
                className="hover:text-primary transition-colors">
                Create account
              </Link>
            </div>

            <p className="text-[11px] text-center text-muted-foreground pt-2">
              By signing in, you agree to our{' '}
              <a
                href="https://app.termly.io/document/terms-of-service/a118a575-bf92-4a88-a954-1589ae572d09"
                target="_blank"
                rel="noopener noreferrer"
                className="underline">
                Terms
              </a>{' '}
              and{' '}
              <a
                href="https://app.termly.io/document/privacy-policy/c16d10b8-1b65-43ea-9568-30e7ce727a60"
                target="_blank"
                rel="noopener noreferrer"
                className="underline">
                Privacy Policy
              </a>
            </p>
          </form>
        </Form>
      </div>
    </div>
  );
}

export function GoogleButton({
  onClick,
  className,
  children,
  ...props
}: React.ComponentProps<typeof Button> & {
  onClick?: () => void;
}) {
  return (
    <Button
      variant="outline"
      onClick={onClick}
      className={cn(className, 'h-10 rounded-sm font-medium', PRESSABLE_CLASS)}
      {...props}>
      <GoogleIcon className="w-4 h-4 mr-2" />
      {children}
    </Button>
  );
}

export default AuthForm;
